import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import {
  Statut, Segmente, Icone, STATUTS, TYPES, libelleType, libelleEchange, dateCourte, quandRelance, messageErreur,
} from './commun';
import Import from './Import';

export default function Comptes({ utilisateur, referentiel, params }) {
  const [statut, setStatut] = useState('tous');
  const [type, setType] = useState('tous');
  const [commercial, setCommercial] = useState('');
  const [societe, setSociete] = useState('');
  const [q, setQ] = useState(params.get('q') || '');
  const [qDiffere, setQDiffere] = useState(q);
  const aPlanifier = params.get('a_planifier') === '1';
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState('');
  const [importer, setImporter] = useState(false);

  useEffect(() => { const t = setTimeout(() => setQDiffere(q), 250); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setQ(params.get('q') || ''); }, [params]);

  const charger = useCallback(async () => {
    try {
      const p = { q: qDiffere || undefined, type: type === 'tous' ? undefined : type, statut: statut === 'tous' ? undefined : statut,
        commercial: commercial || undefined, societe: societe || undefined, a_planifier: aPlanifier ? '1' : undefined };
      setD((await api.get('/commercial/comptes', { params: p })).data); setErreur('');
    } catch (e) { setErreur(messageErreur(e, 'Impossible de charger les comptes.')); }
  }, [qDiffere, type, statut, commercial, societe, aPlanifier]);
  useEffect(() => { charger(); }, [charger]);

  const multiSociete = (referentiel?.societes.length || 0) > 1;
  const optionsStatut = [{ cle: 'tous', libelle: 'Tous', n: d?.total }]
    .concat(STATUTS.map((s) => ({ cle: s.cle, libelle: s.cle === 'client' ? 'Clients' : s.cle === 'prospect' ? 'Prospects' : s.cle === 'perdu' ? 'Perdus' : s.libelle, n: d?.compteurs[s.cle] || 0 })));

  return (
    <div>
      <div className="page-entete">
        <h1>Comptes</h1>
        <div className="entete-actions">
          <button type="button" className="bouton-blanc cache-mobile" onClick={() => setImporter(true)}><Icone.Import /> Importer un fichier Excel</button>
          <a className="bouton bouton-grand cache-mobile" href="#/nouveau"><Icone.Plus /> Nouveau prospect</a>
        </div>
      </div>

      {aPlanifier && (
        <div className="bandeau-info">
          Prospects et comptes en discussion <strong>sans relance prévue</strong>. Ouvrez-les et notez un échange pour leur donner une date.{' '}
          <a href="#/comptes">Voir tous les comptes</a>
        </div>
      )}

      <div className="filtres">
        <Segmente etiquette="Filtrer par statut" valeur={statut} onChange={setStatut} options={optionsStatut} className="defile" />
        <Segmente etiquette="Filtrer par type" valeur={type} onChange={setType} className="defile petit"
          options={[{ cle: 'tous', libelle: 'Tous types' }].concat(TYPES.map((t) => ({ cle: t.cle, libelle: t.libelle })))} />
        {multiSociete && (
          <select value={societe} onChange={(e) => setSociete(e.target.value)} aria-label="Société" className="filtre-select">
            <option value="">Toutes sociétés</option>
            {referentiel.societes.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
          </select>
        )}
        <select value={commercial} onChange={(e) => setCommercial(e.target.value)} aria-label="Commercial" className="filtre-select">
          <option value="">Tous les commerciaux</option>
          {referentiel?.utilisateurs.map((u) => <option key={u.id} value={u.id}>{u.nom || u.identifiant}</option>)}
          <option value="aucun">Sans commercial</option>
        </select>
        <label className="champ-recherche filtre-recherche">
          <Icone.Loupe />
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, ville, contact, code ERP…" aria-label="Rechercher" />
        </label>
      </div>

      {erreur && <div className="alerte">{erreur}</div>}
      {!d && !erreur && <div className="attente">Chargement…</div>}
      {d && (
        <div className="liste-comptes">
          <div className="ligne-compte entete-liste" aria-hidden="true">
            <span>Compte</span><span>Type</span><span>Ville</span><span>Statut</span><span>Dernier échange</span><span>Prochaine action</span>
          </div>
          {d.comptes.map((c) => {
            const qd = quandRelance(c.prochaine_action_le, c.dans_jours);
            return (
              <a key={c.id} className="ligne-compte" href={`#/compte/${c.id}`}>
                <span className="lc-nom">{c.nom}{multiSociete && !societe && <small>{c.societe_nom}</small>}</span>
                <span className="lc-type">{libelleType(c.type)}</span>
                <span className="lc-ville">{c.ville}</span>
                <span className="lc-statut"><Statut statut={c.statut} /></span>
                <span className="lc-dernier">{c.dernier_type ? `${libelleEchange(c.dernier_type)} · ${dateCourte(c.dernier_le)}` : 'Aucun'}</span>
                <span className={`lc-action ${qd.retard ? 'rouge gras' : ''} ${!c.prochaine_action_le ? 'discret' : ''}`}>
                  {c.prochaine_action_le
                    ? `${c.dans_jours === 0 ? "Aujourd'hui" : dateCourte(c.prochaine_action_le)} · ${c.prochaine_action || 'Relancer'}`
                    : c.statut === 'perdu' ? 'Aucune' : c.statut === 'client' ? '—' : 'À planifier'}
                </span>
              </a>
            );
          })}
          {d.comptes.length === 0 && (
            <div className="vide">
              {d.total === 0 && !qDiffere && type === 'tous'
                ? <><p><strong>Aucun compte pour l'instant.</strong></p><p>Importez votre liste clients et vos fichiers de prospects, ou créez un premier prospect.</p></>
                : <p>Aucun compte ne correspond à ces filtres.</p>}
            </div>
          )}
        </div>
      )}
      {d && d.comptes.length >= 1000 && <p className="discret">Seuls les 1 000 premiers comptes sont affichés : affinez la recherche.</p>}

      {importer && <Import utilisateur={utilisateur} referentiel={referentiel} onFerme={() => setImporter(false)} onFini={charger} />}
    </div>
  );
}
